import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import DropActionsModal from './DropActionsModal';

interface DropClaimOverlayProps {
  collectionId: string;
  onClose: () => void;
}

interface Drop {
  id: string;
  rarity: string;
  serial_number: string;
  media_url: string;
}

const CARD_BACK_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)';

const DropClaimOverlay: React.FC<DropClaimOverlayProps> = ({ collectionId, onClose }) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [phase, setPhase] = useState<'acquiring' | 'selecting' | 'revealing' | 'claimed'>('acquiring');
  const [cards, setCards] = useState<Drop[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [revealedCards, setRevealedCards] = useState<boolean[]>([false, false, false, false]);
  const [claimedDrop, setClaimedDrop] = useState<Drop | null>(null);
  const [showActions, setShowActions] = useState(false);

  useEffect(() => {
    // Phase 1: Show "DROP ACQUIRED" for 2 seconds
    const timer = setTimeout(() => {
      setPhase('selecting');
      loadCardsForSelection();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const loadCardsForSelection = async () => {
    try {
      // Get 4 random unclaimed drops from this collection
      const { data, error } = await supabase
        .from('drops')
        .select('id, rarity, serial_number, media_url')
        .eq('collection_id', collectionId)
        .eq('claimed', false)
        .limit(4);

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('No drops available');
      }

      // Shuffle and take 4
      const shuffled = data.sort(() => Math.random() - 0.5);
      setCards(shuffled.slice(0, 4));
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      onClose();
    }
  };

  const handleCardSelect = (index: number) => {
    if (phase !== 'selecting' || selectedIndex !== null) return;
    
    setSelectedIndex(index);
    setPhase('revealing');
    
    // Reveal cards one by one, user's card last
    const revealSequence = [0, 1, 2, 3].filter(i => i !== index).concat([index]);
    
    revealSequence.forEach((cardIndex, i) => {
      setTimeout(() => {
        setRevealedCards(prev => {
          const next = [...prev];
          next[cardIndex] = true;
          return next;
        });
        
        // If this is the last card (user's selection), claim it
        if (cardIndex === index) {
          setTimeout(() => claimDrop(cards[index]), 1000);
        }
      }, i * 600);
    });
  };

  const claimDrop = async (drop: Drop) => {
    try {
      // Mark drop as claimed
      const { error: dropError } = await supabase
        .from('drops')
        .update({
          claimed: true,
          user_id: user!.id,
          claimed_at: new Date().toISOString(),
        })
        .eq('id', drop.id);

      if (dropError) throw dropError;

      // Record in user_drop_views
      const { error: viewError } = await supabase
        .from('user_drop_views')
        .upsert({
          user_id: user!.id,
          drop_collection_id: collectionId,
          claimed: true,
          claimed_at: new Date().toISOString(),
          drop_id: drop.id,
        });

      if (viewError) throw viewError;

      setClaimedDrop(drop);
      setPhase('claimed');
      
      // Show actions modal after 2 seconds
      setTimeout(() => setShowActions(true), 2000);
    } catch (error: any) {
      toast({
        title: 'Claim failed',
        description: error.message,
        variant: 'destructive',
      });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      <AnimatePresence mode="wait">
        {phase === 'acquiring' && (
          <motion.div
            key="acquiring"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            className="text-center"
          >
            <motion.div
              animate={{
                rotate: 360,
                scale: [1, 1.1, 1],
              }}
              transition={{
                rotate: { duration: 1, repeat: Infinity, ease: 'linear' },
                scale: { duration: 0.5, repeat: Infinity, repeatType: 'reverse' },
              }}
              className="w-48 h-48 mx-auto mb-8 rounded-2xl shadow-2xl"
              style={{
                background: CARD_BACK_GRADIENT,
              }}
            />
            <motion.h1
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatType: 'reverse' }}
              className="text-6xl font-bold text-white tracking-wider"
              style={{
                textShadow: '0 0 30px rgba(255,255,255,0.5), 0 0 60px rgba(138,43,226,0.5)',
              }}
            >
              DROP ACQUIRED
            </motion.h1>
          </motion.div>
        )}

        {phase === 'selecting' && (
          <motion.div
            key="selecting"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full max-w-4xl px-8"
          >
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Choose Your Card
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {cards.map((card, index) => (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -10 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleCardSelect(index)}
                  className="aspect-[2/3] rounded-xl shadow-2xl cursor-pointer relative overflow-hidden"
                  style={{
                    background: CARD_BACK_GRADIENT,
                  }}
                >
                  <motion.div
                    animate={{ opacity: [0.5, 0.8, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 bg-white/10"
                  />
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-6xl text-white/50">?</span>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {showActions && claimedDrop && (
          <DropActionsModal
            drop={claimedDrop}
            onClose={onClose}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DropClaimOverlay;
